/* The blog's motion in script, in the site's language (canvas-project/Motion.dc.html): things transform, nothing
   fades, on the closed-form springs. The openers' entrance is set before the first paint by an inline script
   (partials/motion-entrance.hbs); the moving itself is CSS (assets/css/parts/motion.css). gulp concatenates this
   before main.js, which mounts it. Four parts:

   Arrivals. Cards lift 56px from a scale of 0.97 and their topic pill pops in after them; the band, the topic chips
   and an article's headings rise 24px; section titles set from behind their own lower edge: each as it comes into
   view, the board's beat 2, on the default spring (k170 d26), the heavy one (k90 d20) for titles. An
   IntersectionObserver lets each go once an eighth of it is in view, above the phone's bottom bar where the bar shows,
   and the CSS moves it in time on the spring's curve, so the arrival plays however the page is scrolled: the site's
   own reveal where scroll timelines are missing (components/roadside/take/clock.js, its .io path), used everywhere.
   Parts that go together follow each other by 80ms in reading order, across a row of cards. What is in view when the
   page starts never moves; nothing is held back without script, with reduced motion, or in print.

   Magnets. Every .mag button leans toward a fine pointer within 80px of its edge, a mouse or a laptop's trackpad
   alike (both are fine pointers that hover), on the "pointer spring k320 d30" (the board's beat 6 and its house rule
   "snappy k320 d30 for buttons and edges"), with the pull of the site's own magnets (clock.js), 22% of the way across
   and 30% of the way up, at most 12px either way. The spring is the site's closed form (components/roadside/take/
   kit.js, spring), solved from wherever the button is and however fast it moves each time the pull changes, so a
   button is where the formula puts it at any frame rate. The header's call has a twin in the light layer, which leans
   with it. Off for a touch or other coarse pointer, for reduced motion, and while the page is hidden.

   Back to top. An article's pill slides up from the bottom right once the reader is 800px down and away again above
   it; a press scrolls to the top (at once with reduced motion) and puts the keyboard at the start of the page.

   The reading line. Where a browser has no scroll timelines, this draws the article's reading line itself. */
var cyMotion = (function () {
    'use strict';

    var REACH = 80;
    var PULL_X = 0.22;
    var PULL_Y = 0.3;
    var MAX_X = 12;
    var MAX_Y = 12;
    var STIFFNESS = 320;
    var DAMPING = 30;
    /* Below this, in px and px per second, a button is at rest. */
    var REST = 0.01;

    /* What arrives, how much of it is in view when it goes, the gap between parts that go together and the most of
       those gaps one part waits. */
    var ARRIVE = '.card, .band, .topics, .rv, .gh-content h2, .gh-content h3';
    var ARRIVE_AT = 0.12;
    var STAGGER = 80;
    var STAGGER_MAX = 5;

    /* How far down the back to top pill comes up. */
    var TO_TOP_AFTER = 800;

    /* k320 d30 on a unit mass: it decays at d/2 = 15 per second and swings at sqrt(k - (d/2)^2) = 9.75 radians per
       second, a damping ratio of 0.84, just under critical. */
    var DECAY = DAMPING / 2;
    var SWING = Math.sqrt(STIFFNESS - DECAY * DECAY);

    function clamp(x, a, b) {
        return Math.min(b, Math.max(a, x));
    }

    /* The closed-form spring: how far from its target a value is t seconds after it was let go e0 from it at speed
       v0, and its speed then. The site's spring(t, 320, 30), a step from 0 to 1, is 1 + settle(-1, 0, t).e. */
    function settle(e0, v0, t) {
        var fall = Math.exp(-DECAY * t);
        var c = Math.cos(SWING * t);
        var s = Math.sin(SWING * t);
        return {
            e: fall * (e0 * c + ((v0 + DECAY * e0) / SWING) * s),
            v: fall * (v0 * c - ((DECAY * v0 + STIFFNESS * e0) / SWING) * s)
        };
    }

    /* One axis of a button: let go at time t0 (seconds), e0 from its target `to`, at speed v0. */
    function axis() {
        return { to: 0, e0: 0, v0: 0, t0: 0 };
    }

    /* Where an axis is at time t, and how fast it moves. */
    function read(ax, t) {
        var s = settle(ax.e0, ax.v0, Math.max(0, t - ax.t0));
        return { x: ax.to + s.e, v: s.v };
    }

    /* A new target at time t: the spring carries on from where the button is, at the speed it moves. */
    function retarget(ax, to, t) {
        if (to === ax.to) {
            return;
        }
        var now = read(ax, t);
        ax.e0 = now.x - to;
        ax.v0 = now.v;
        ax.t0 = t;
        ax.to = to;
    }

    /* Where a button leans for a pointer at (px, py): rect is the button's box as drawn, shown is where it leans now,
       so the box it would have at rest is the rect less that offset. Zero beyond the reach. */
    function target(px, py, rect, shown) {
        var cx = rect.left + rect.width / 2 - shown.x;
        var cy = rect.top + rect.height / 2 - shown.y;
        var dx = Math.max(Math.abs(px - cx) - rect.width / 2, 0);
        var dy = Math.max(Math.abs(py - cy) - rect.height / 2, 0);
        if (Math.sqrt(dx * dx + dy * dy) >= REACH) {
            return { x: 0, y: 0 };
        }
        return { x: clamp((px - cx) * PULL_X, -MAX_X, MAX_X), y: clamp((py - cy) * PULL_Y, -MAX_Y, MAX_Y) };
    }

    function paint(m) {
        var value = m.shown.x === 0 && m.shown.y === 0 ? '' : 'translate3d(' + m.shown.x.toFixed(2) + 'px,' + m.shown.y.toFixed(2) + 'px,0)';
        m.el.style.transform = value;
        if (m.twin) {
            m.twin.style.transform = value;
        }
    }

    function matches(win, query) {
        return typeof win.matchMedia === 'function' && win.matchMedia(query).matches;
    }

    /* The height the phone's bottom bar covers at the foot of the window, when it shows (parts/footer.css). */
    function barHeight(win, doc) {
        var bar = doc.querySelector('.bar');
        return bar && win.getComputedStyle(bar).display !== 'none' ? Math.round(bar.getBoundingClientRect().height) : 0;
    }

    /* Starts the arrivals; returns null when they are off (reduced motion, no IntersectionObserver, nothing to
       arrive), or the function that stops them and leaves everything at rest. */
    function arrivals(win, doc) {
        if (matches(win, '(prefers-reduced-motion: reduce)') || typeof win.IntersectionObserver !== 'function') {
            return null;
        }
        var found = doc.querySelectorAll(ARRIVE);
        if (!found.length) {
            return null;
        }
        var bar = barHeight(win, doc);
        var edge = win.innerHeight - bar;
        var waiting = [];
        // What is already in view, or above it, arrives at once, in the same frame as the class that holds the rest
        // back, so nothing on screen ever moves when the page starts.
        for (var i = 0; i < found.length; i += 1) {
            if (found[i].getBoundingClientRect().top < edge) {
                found[i].classList.add('cy-in');
            } else {
                waiting.push(found[i]);
            }
        }
        var root = doc.documentElement;
        root.classList.add('cy-motion');
        var io = new win.IntersectionObserver(function (entries) {
            // in reading order, so a row of cards goes left to right
            var going = [];
            for (var j = 0; j < entries.length; j += 1) {
                if (entries[j].isIntersecting) {
                    going.push({ el: entries[j].target, box: entries[j].boundingClientRect });
                }
            }
            going.sort(function (a, b) {
                return Math.abs(a.box.top - b.box.top) > 8 ? a.box.top - b.box.top : a.box.left - b.box.left;
            });
            for (var g = 0; g < going.length; g += 1) {
                going[g].el.style.setProperty('--cy-delay', Math.min(g, STAGGER_MAX) * STAGGER + 'ms');
                going[g].el.classList.add('cy-in');
                io.unobserve(going[g].el);
            }
        }, { rootMargin: '0px 0px ' + -bar + 'px 0px', threshold: ARRIVE_AT });
        for (var k = 0; k < waiting.length; k += 1) {
            io.observe(waiting[k]);
        }
        return function stop() {
            io.disconnect();
            root.classList.remove('cy-motion');
        };
    }

    /* Starts the magnets on a page; returns null when they are off (reduced motion, a coarse pointer, no .mag), or
       the function that stops them. */
    function magnets(win, doc) {
        if (matches(win, '(prefers-reduced-motion: reduce)') || !matches(win, '(hover: hover) and (pointer: fine)')) {
            return null;
        }
        var found = doc.querySelectorAll('.mag');
        var mags = [];
        for (var i = 0; i < found.length; i += 1) {
            var el = found[i];
            var layer = el.closest ? el.closest('[data-cy-layer="dark"]') : null;
            var twin = layer && el.classList.contains('call') ? layer.parentNode.querySelector('[data-cy-layer="light"] .call') : null;
            mags.push({ el: el, twin: twin, x: axis(), y: axis(), shown: { x: 0, y: 0 } });
        }
        if (!mags.length) {
            return null;
        }

        var px = -1e4;
        var py = -1e4;
        var raf = 0;

        function frame(time) {
            raf = 0;
            if (doc.hidden) {
                return;
            }
            var t = time / 1000;
            var moving = false;
            for (var j = 0; j < mags.length; j += 1) {
                var m = mags[j];
                var rect = m.el.getBoundingClientRect();
                var onScreen = rect.bottom > 0 && rect.top < win.innerHeight;
                var lean = onScreen ? target(px, py, rect, m.shown) : { x: 0, y: 0 };
                retarget(m.x, lean.x, t);
                retarget(m.y, lean.y, t);
                var x = read(m.x, t);
                var y = read(m.y, t);
                if (Math.abs(x.x - m.x.to) < REST && Math.abs(x.v) < REST && Math.abs(y.x - m.y.to) < REST && Math.abs(y.v) < REST) {
                    // at rest: settle exactly on the target, so a button let go comes back to no transform at all
                    m.x = { to: m.x.to, e0: 0, v0: 0, t0: t };
                    m.y = { to: m.y.to, e0: 0, v0: 0, t0: t };
                    m.shown = { x: m.x.to, y: m.y.to };
                } else {
                    m.shown = { x: x.x, y: y.x };
                    moving = true;
                }
                paint(m);
            }
            if (moving) {
                raf = win.requestAnimationFrame(frame);
            }
        }

        function wake() {
            if (!raf && !doc.hidden) {
                raf = win.requestAnimationFrame(frame);
            }
        }

        function onPointer(event) {
            px = event.clientX;
            py = event.clientY;
            wake();
        }

        /* The pointer left the window: every button lets go. A pointerout with no element to go to is the leave
           that every engine sends to the window. */
        function onOut(event) {
            if (!event.relatedTarget) {
                px = -1e4;
                py = -1e4;
                wake();
            }
        }

        win.addEventListener('pointermove', onPointer, { passive: true });
        win.addEventListener('scroll', wake, { passive: true });
        win.addEventListener('pointerout', onOut, { passive: true });
        doc.addEventListener('visibilitychange', wake);

        return function stop() {
            win.removeEventListener('pointermove', onPointer);
            win.removeEventListener('scroll', wake);
            win.removeEventListener('pointerout', onOut);
            doc.removeEventListener('visibilitychange', wake);
            if (raf) {
                win.cancelAnimationFrame(raf);
                raf = 0;
            }
            for (var k = 0; k < mags.length; k += 1) {
                mags[k].x = axis();
                mags[k].y = axis();
                mags[k].shown = { x: 0, y: 0 };
                paint(mags[k]);
            }
        };
    }

    /* The back to top pill: shown once the page is TO_TOP_AFTER down. Returns null without the pill, or the function
       that stops it. It runs with reduced motion too, as a control; only its slide is off then (the CSS). */
    function toTop(win, doc) {
        var pill = doc.querySelector('[data-cy-to-top]');
        if (!pill) {
            return null;
        }
        var raf = 0;
        var shown = false;
        function update() {
            raf = 0;
            var show = (win.scrollY || win.pageYOffset || 0) > TO_TOP_AFTER;
            if (show !== shown) {
                shown = show;
                pill.classList.toggle('cy-show', show);
            }
        }
        function schedule() {
            if (!raf) {
                raf = win.requestAnimationFrame(update);
            }
        }
        function onClick(event) {
            event.preventDefault();
            win.scrollTo({ top: 0, behavior: matches(win, '(prefers-reduced-motion: reduce)') ? 'auto' : 'smooth' });
            var main = doc.getElementById('main-content');
            if (main && main.focus) {
                main.focus({ preventScroll: true });
            }
        }
        update();
        win.addEventListener('scroll', schedule, { passive: true });
        pill.addEventListener('click', onClick);
        return function stop() {
            win.removeEventListener('scroll', schedule);
            pill.removeEventListener('click', onClick);
            if (raf) {
                win.cancelAnimationFrame(raf);
                raf = 0;
            }
        };
    }

    /* The reading line where a browser has no scroll timelines (where it has them, the CSS draws it): the share of the
       article read, from its top at the top of the window to its end at the bottom of the window. Returns null where
       it is not needed (no article, scroll timelines, reduced motion), or the function that stops it. */
    function reading(win, doc) {
        var line = doc.querySelector('.read-progress');
        var article = doc.querySelector('.cy-article');
        var css = win.CSS;
        if (!line || !article || (css && css.supports && css.supports('animation-timeline: view()')) || matches(win, '(prefers-reduced-motion: reduce)')) {
            return null;
        }
        var raf = 0;
        function update() {
            raf = 0;
            var box = article.getBoundingClientRect();
            var span = box.height - win.innerHeight;
            var share = span > 0 ? Math.min(1, Math.max(0, -box.top / span)) : (box.top < 0 ? 1 : 0);
            line.style.transform = 'scaleX(' + share.toFixed(4) + ')';
        }
        function schedule() {
            if (!raf) {
                raf = win.requestAnimationFrame(update);
            }
        }
        line.classList.add('cy-read-js');
        update();
        win.addEventListener('scroll', schedule, { passive: true });
        win.addEventListener('resize', schedule);
        return function stop() {
            win.removeEventListener('scroll', schedule);
            win.removeEventListener('resize', schedule);
            line.classList.remove('cy-read-js');
            if (raf) {
                win.cancelAnimationFrame(raf);
                raf = 0;
            }
        };
    }

    /* Every part; each is null where it is off. */
    function mount(win, doc) {
        return { arrivals: arrivals(win, doc), magnets: magnets(win, doc), toTop: toTop(win, doc), reading: reading(win, doc) };
    }

    return {
        REACH: REACH,
        STIFFNESS: STIFFNESS,
        DAMPING: DAMPING,
        ARRIVE: ARRIVE,
        ARRIVE_AT: ARRIVE_AT,
        STAGGER: STAGGER,
        STAGGER_MAX: STAGGER_MAX,
        TO_TOP_AFTER: TO_TOP_AFTER,
        MAX: MAX_X,
        settle: settle,
        target: target,
        arrivals: arrivals,
        magnets: magnets,
        toTop: toTop,
        reading: reading,
        mount: mount
    };
})();
